import { t, useTranslation } from "@redotech/redo-i18n";
import {
  SetStatusActionStatus,
  SetStatusSnoozePeriod,
} from "@redotech/redo-return-core-common/return-flow/action-enums";
import * as selectCss from "@redotech/redo-return-ui/flows/return-flow/select.module.css";
import { RuleTypeDetailsProps } from "@redotech/redo-return-ui/flows/rules/multiple-actions";
import { SetStatusState } from "@redotech/redo-return-ui/flows/rules/set-status";
import { LabeledInput } from "@redotech/redo-web/labeled-input";
import { SelectDropdown } from "@redotech/redo-web/select-dropdown";
import { memo } from "react";

const statusOptions = [
  SetStatusActionStatus.ARCHIVED,
  SetStatusActionStatus.CLOSED,
  SetStatusActionStatus.OPEN,
  SetStatusActionStatus.SNOOZED,
  SetStatusActionStatus.SPAM,
];

const snoozePeriodOptions = [
  SetStatusSnoozePeriod.NEXT_WEEK,
  SetStatusSnoozePeriod.TODAY,
  SetStatusSnoozePeriod.TOMORROW,
  SetStatusSnoozePeriod.WEEKEND,
];

const getSnoozePeriodLabel = (snoozePeriod: SetStatusSnoozePeriod) => {
  switch (snoozePeriod) {
    case SetStatusSnoozePeriod.TODAY:
      return t("Later the same day");
    case SetStatusSnoozePeriod.TOMORROW:
      return t("The next day");
    case SetStatusSnoozePeriod.WEEKEND:
      return t("The next Saturday");
    case SetStatusSnoozePeriod.NEXT_WEEK:
      return t("The next Monday");
  }
};

const getStatusLabel = (status: SetStatusActionStatus) => {
  switch (status) {
    case SetStatusActionStatus.ARCHIVED:
      return t("Archived", { context: "support ticket status" });
    case SetStatusActionStatus.CLOSED:
      return t("Closed", { context: "support ticket status" });
    case SetStatusActionStatus.OPEN:
      return t("Open", { context: "support ticket status" });
    case SetStatusActionStatus.SNOOZED:
      return t("Snoozed", { context: "support ticket status" });
    case SetStatusActionStatus.SPAM:
      return t("Spam", { context: "support ticket status" });
  }
};

export const SetStatusDetails = memo(function SetStatusDetails({
  state,
  setState,
  disabled,
}: RuleTypeDetailsProps<SetStatusState>) {
  const { t } = useTranslation();
  return (
    <>
      <LabeledInput label={t("Status", { context: "support ticket status" })}>
        <SelectDropdown
          disabled={disabled}
          options={statusOptions}
          value={state.status}
          valueChange={(e) => {
            if (e) {
              setState((state) => ({ ...state, status: e }));
            }
          }}
        >
          {(status) => (
            <div className={selectCss.value}>
              <div className={selectCss.title}>{getStatusLabel(status)}</div>
            </div>
          )}
        </SelectDropdown>
      </LabeledInput>
      {state.status === SetStatusActionStatus.SNOOZED && (
        <LabeledInput label={t("Until")}>
          <SelectDropdown
            disabled={disabled}
            options={snoozePeriodOptions}
            value={state.snoozePeriod}
            valueChange={(e) => {
              if (e) {
                setState((state) => ({ ...state, snoozePeriod: e }));
              }
            }}
          >
            {(snoozePeriod) => (
              <div className={selectCss.value}>
                <div className={selectCss.title}>
                  {getSnoozePeriodLabel(snoozePeriod)}
                </div>
              </div>
            )}
          </SelectDropdown>
        </LabeledInput>
      )}
    </>
  );
});
